import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ConversationSentimentTypeEnum } from '@/Domain/enum/ConversationSentimentType.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';

type Props = {
  conversation: ConversationDto;
};

const chartConfig = [{ value: 1 }, { value: 1 }, { value: 1 }];

export const UnifiedSentiment = ({ conversation }: Props) => {
  const { t } = useTranslation('inbox_v2');

  const unifiedAnalysis = conversation.unified_analysis;

  if (!unifiedAnalysis || !unifiedAnalysis.sentiment) {
    return (
      <Tooltip>
        <div className="flex flex-col gap-2">
          <section className="flex items-center gap-1 text-foreground-subtle">
            <p className="font-semibold text-foreground-subtle">
              {t('conversation_details.sentiment.title')}
            </p>
            <TooltipTrigger>
              <Info className="size-4" />
            </TooltipTrigger>
            <TooltipContent className="absolute left-8 -translate-y-24 -translate-x-1/2 w-[250px]">
              {t('tooltip.sentiment')}
            </TooltipContent>
          </section>
          <p className="text-foreground-subtle loading-text">
            {t('conversation_details.unified_analytics.pending')}
          </p>
        </div>
      </Tooltip>
    );
  }

  const config = ConversationHelper.getSentimentConfig(
    unifiedAnalysis.sentiment as ConversationSentimentTypeEnum,
  );

  return (
    <>
      <Tooltip>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-foreground-subtle mb-2">
            <section className="flex items-center gap-1 text-foreground-subtle">
              <p className="font-semibold text-foreground-subtle">
                {t('conversation_details.sentiment.title')}
              </p>
              <TooltipTrigger>
                <Info className="size-4" />
              </TooltipTrigger>
              <TooltipContent className="absolute left-8 -translate-y-24 -translate-x-1/2 w-[250px]">
                {t('tooltip.sentiment')}
              </TooltipContent>
            </section>

            <section className="relative">
              <PieChart width={90} height={35}>
                <Pie
                  data={chartConfig}
                  cx={50}
                  cy={25}
                  dataKey="value"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={24}
                  outerRadius={30}
                  paddingAngle={5}
                  animationBegin={0}
                  animationDuration={300}
                >
                  {chartConfig.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={config.colors[index]} stroke="none" />
                  ))}
                </Pie>
              </PieChart>
              <span
                className="text-md absolute top-4 left-11 transform translate-x-0.5"
                dangerouslySetInnerHTML={{ __html: config.icon }}
              />
            </section>
          </div>
          <div>{config.label ? <Badge variant="secondary">{config.label}</Badge> : null}</div>

          <p className="text-foreground-subtle">{config?.description}</p>
        </div>
      </Tooltip>
    </>
  );
};
